/**
 * `decrireFigure(spec)` — F4 de `docs/produit/SPEC-REPRESENTATION-VISUELLE.md`
 * (§3.1.3, §6.10, §10.6).
 *
 * **La description textuelle n'est JAMAIS écrite par le modèle.** Elle est
 * produite ici, déterministement, depuis la figure RÉSOLUE. Si le modèle
 * l'écrivait, elle pourrait contredire le dessin — or pour un élève
 * dyslexique, pour un parent et pour un lecteur d'écran, cette phrase EST
 * l'énoncé. Une légende fausse est un énoncé faux pour ceux que le produit
 * sert.
 *
 * La même chaîne est servie à trois endroits, sans jamais diverger :
 * `accessibilityLabel` du SVG, légende VISIBLE `.fig-cap`, texte alternatif de
 * l'image du courriel.
 *
 * Deux règles de rédaction, imposées par la maquette (§6.10.5) :
 *  - ce sont des PHRASES, pas des listes de coordonnées ;
 *  - la position est donnée en MOTS (« en haut », « à droite »), lue sur la
 *    figure résolue — donc juste même après une rotation.
 *
 * Registre : la description est lue par l'élève, le parent ET le tuteur. Elle
 * est donc purement descriptive — ni tutoiement ni vouvoiement, aucun jugement.
 */
import { formaterNombre, rad } from './geometrie.js';
import { resoudreFigure } from './resoudre.js';
const mes = (v, u) => `${formaterNombre(v)} ${u}`;
const cote = (a, b) => `[${a}${b}]`;
/** Position relative EN MOTS, lue sur la figure résolue (§6.10.5). */
export function positionEnMots(p, boite) {
    const vertical = p.y < boite.hauteur / 3 ? 'en haut' : p.y > (2 * boite.hauteur) / 3 ? 'en bas' : '';
    const horizontal = p.x < boite.largeur / 3 ? 'à gauche' : p.x > (2 * boite.largeur) / 3 ? 'à droite' : '';
    if (vertical !== '' && horizontal !== '')
        return `${vertical} ${horizontal}`;
    if (vertical !== '')
        return vertical;
    if (horizontal !== '')
        return horizontal;
    return 'au centre';
}
function assembler(phrases) {
    return phrases
        .map((p) => p.trim())
        .filter((p) => p.length > 0)
        .map((p) => (/[.!?]$/.test(p) ? p : `${p}.`))
        .join(' ');
}
function ou(resolue, nom) {
    const p = resolue.points.find((q) => q.nom === nom);
    return p === undefined ? '' : positionEnMots(p.position, resolue.boite);
}
/* --------------------------------------------------------------- triangle */
function decrireTriangle(spec, r) {
    const [A, B, C] = spec.sommets;
    const phrases = [`Triangle ${A}${B}${C}`];
    if (spec.construction === 'rectangle' && spec.sommetAngleDroit !== undefined) {
        const position = ou(r, spec.sommetAngleDroit);
        phrases.push(`L'angle droit est marqué au sommet ${spec.sommetAngleDroit}${position === '' ? '' : `, ${position}`}`);
    }
    const cotes = spec.donnees.filter((d) => d.type === 'cote');
    if (cotes.length > 0) {
        phrases.push(cotes
            .map((d) => d.type === 'cote' ? `le côté ${cote(d.de, d.a)} mesure ${mes(d.valeur, d.unite)}` : '')
            .join(', ')
            .replace(/^./, (c) => c.toUpperCase()));
    }
    for (const d of spec.donnees) {
        if (d.type === 'angle') {
            phrases.push(`Un angle de ${formaterNombre(d.degres)}° est marqué au sommet ${d.sommet}`);
        }
    }
    for (const e of spec.etiquettes ?? []) {
        if (e.ancrage.type === 'cote')
            phrases.push(`Le côté ${cote(e.ancrage.de, e.ancrage.a)} porte l'étiquette ${e.texte}`);
        else if (e.ancrage.type === 'sommet')
            phrases.push(`Le sommet ${e.ancrage.nom} porte l'étiquette ${e.texte}`);
        else if (e.ancrage.type === 'angle')
            phrases.push(`L'angle au sommet ${e.ancrage.sommet} porte l'étiquette ${e.texte}`);
    }
    if ((spec.marquesEgalite ?? []).length > 0) {
        phrases.push(`Des marques d'égalité sont posées sur la figure`);
    }
    if (spec.hauteurDepuis !== undefined) {
        phrases.push(`La hauteur issue du sommet ${spec.hauteurDepuis} est tracée en pointillé`);
    }
    if ((spec.rotationDeg ?? 0) !== 0)
        phrases.push('La figure est posée de biais');
    return assembler(phrases);
}
/* ----------------------------------------------------------------- cercle */
function decrireCercle(spec, _r) {
    const phrases = [`Cercle de centre ${spec.nomCentre}`];
    if (spec.rayonAffiche === true)
        phrases.push(`Son rayon mesure ${mes(spec.rayon, spec.unite)}`);
    for (const el of spec.elements ?? []) {
        switch (el.type) {
            case 'rayon':
                phrases.push(`Un segment est tracé du centre jusqu'au bord${el.mesureAffichee === true ? ` ; il mesure ${mes(spec.rayon, spec.unite)}` : ''}`);
                break;
            case 'diametre':
                phrases.push(`Un segment traverse le cercle en passant par son centre, d'un bord à l'autre${el.mesureAffichee === true ? ` ; il mesure ${mes(2 * spec.rayon, spec.unite)}` : ''}`);
                break;
            case 'corde': {
                const longueur = 2 * spec.rayon * Math.abs(Math.sin(rad(el.angleDeg2 - el.angleDeg1) / 2));
                phrases.push(`Une corde relie deux points du bord du cercle${el.mesureAffichee === true ? ` ; elle mesure ${mes(longueur, spec.unite)}` : ''}`);
                break;
            }
            case 'secteur': {
                const ouverture = Math.abs(el.angleFinDeg - el.angleDebutDeg);
                phrases.push(`Un secteur d'ouverture ${formaterNombre(ouverture)}° est coloré`);
                break;
            }
            case 'arc': {
                const ouverture = Math.abs(el.angleFinDeg - el.angleDebutDeg);
                const longueur = spec.rayon * rad(ouverture);
                phrases.push(`Un arc d'ouverture ${formaterNombre(ouverture)}° est tracé sur le bord${el.mesureAffichee === true ? ` ; il mesure ${mes(longueur, spec.unite)}` : ''}`);
                break;
            }
        }
    }
    for (const e of spec.etiquettes ?? [])
        phrases.push(`La figure porte l'étiquette ${e.texte}`);
    return assembler(phrases);
}
/* --------------------------------------------------------------- polygone */
const NOM_POLYGONE = {
    rectangle: 'Rectangle',
    carre: 'Carré',
    parallelogramme: 'Parallélogramme',
    losange: 'Losange',
    trapeze: 'Trapèze',
    regulier: 'Polygone régulier',
};
function decrirePolygone(spec, _r) {
    const d = spec.dimensions;
    const nom = d.forme === 'regulier' ? `Polygone régulier à ${d.nbCotes} côtés` : NOM_POLYGONE[d.forme];
    const phrases = [`${nom} ${spec.sommets.join('')}`];
    switch (d.forme) {
        case 'rectangle':
            phrases.push(`Il mesure ${mes(d.largeur, d.unite)} de large et ${mes(d.hauteur, d.unite)} de haut`);
            break;
        case 'carre':
            phrases.push(`Chaque côté mesure ${mes(d.cote, d.unite)}`);
            break;
        case 'parallelogramme':
            phrases.push(`La base mesure ${mes(d.base, d.unite)}, le côté oblique ${mes(d.cote, d.unite)}, et l'angle à la base vaut ${formaterNombre(d.angleDeg)}°`);
            break;
        case 'losange':
            phrases.push(`Chaque côté mesure ${mes(d.cote, d.unite)}, et l'angle à la base vaut ${formaterNombre(d.angleDeg)}°`);
            break;
        case 'trapeze':
            phrases.push(`La petite base mesure ${mes(d.petiteBase, d.unite)} en haut, la grande base ${mes(d.grandeBase, d.unite)} en bas, et la hauteur ${mes(d.hauteur, d.unite)}`);
            break;
        case 'regulier':
            phrases.push(`Chaque côté mesure ${mes(d.cote, d.unite)}`);
            break;
    }
    if (spec.hauteurTracee === true)
        phrases.push("La hauteur est tracée en pointillé, avec sa marque d'angle droit");
    if (spec.apothemeTrace === true)
        phrases.push("L'apothème est tracé en pointillé");
    for (const e of spec.etiquettes ?? [])
        phrases.push(`La figure porte l'étiquette ${e.texte}`);
    if ((spec.rotationDeg ?? 0) !== 0)
        phrases.push('La figure est posée de biais');
    return assembler(phrases);
}
/* ----------------------------------------------------------------- repere */
const COURBE_EN_MOTS = {
    lineaire: 'une droite',
    quadratique: 'une parabole',
    sinusoidale: 'une courbe sinusoïdale',
    exponentielle: 'une courbe exponentielle',
};
const coord = (x, y) => `(${formaterNombre(x)} ; ${formaterNombre(y)})`;
function decrireRepere(spec, r) {
    const { xMin, xMax, yMin, yMax } = spec.domaine;
    const phrases = [
        `Plan cartésien${spec.grille === false ? '' : ' quadrillé'}, gradué de ${formaterNombre(xMin)} à ${formaterNombre(xMax)} en abscisse et de ${formaterNombre(yMin)} à ${formaterNombre(yMax)} en ordonnée`,
    ];
    for (const o of spec.objets) {
        switch (o.type) {
            case 'point': {
                const position = ou(r, o.nom);
                phrases.push(`Le point ${o.nom} est marqué en ${coord(o.x, o.y)}${position === '' ? '' : `, ${position}`}`);
                break;
            }
            case 'segment': {
                const de = o.de.type === 'coordonnees' ? coord(o.de.x, o.de.y) : `le point ${o.de.nom}`;
                const a = o.a.type === 'coordonnees' ? coord(o.a.x, o.a.y) : `le point ${o.a.nom}`;
                phrases.push(`Un segment relie ${de} et ${a}${o.etiquette === undefined ? '' : ` ; il porte l'étiquette ${o.etiquette}`}`);
                break;
            }
            case 'droite':
                phrases.push(`Une droite d'équation y = ${formaterNombre(o.a)}x ${o.b < 0 ? '−' : '+'} ${formaterNombre(Math.abs(o.b))} traverse le repère`);
                break;
            case 'vecteur': {
                const origine = o.origine.type === 'coordonnees'
                    ? coord(o.origine.x, o.origine.y)
                    : `le point ${o.origine.nom}`;
                const sens = o.composantes.x >= 0 && o.composantes.y >= 0
                    ? 'vers la droite en montant'
                    : o.composantes.x >= 0
                        ? 'vers la droite en descendant'
                        : o.composantes.y >= 0
                            ? 'vers la gauche en montant'
                            : 'vers la gauche en descendant';
                phrases.push(`Le vecteur ${o.nom} part de ${origine} et va ${sens}, de ${formaterNombre(o.composantes.x)} en abscisse et ${formaterNombre(o.composantes.y)} en ordonnée`);
                break;
            }
            case 'courbe': {
                const points = o.fonction.pointsAMarquer ?? [];
                const marques = points.length === 0
                    ? ''
                    : ` ; ${points.map((p) => `le point ${p.etiquette} est marqué en ${coord(p.x, p.y)}`).join(', ')}`;
                phrases.push(`${COURBE_EN_MOTS[o.fonction.famille]} est tracée${o.etiquette === undefined ? '' : `, étiquetée ${o.etiquette}`}${marques}`.replace(/^./, (c) => c.toUpperCase()));
                break;
            }
        }
    }
    return assembler(phrases);
}
/* -------------------------------------------------------------- diagramme */
function decrireDiagramme(spec, _r) {
    const phrases = [];
    if (spec.type === 'nuage_de_points') {
        const pts = spec.points ?? [];
        phrases.push(`Nuage de ${pts.length} points`);
        if (spec.droiteRegression !== undefined)
            phrases.push('Une droite de régression est tracée en pointillé');
    }
    else {
        const series = spec.series ?? [];
        phrases.push(spec.type === 'bandes'
            ? `Diagramme à bandes de ${series.length} barres`
            : `Ligne brisée reliant ${series.length} points`);
        phrases.push(series
            .map((s) => `${s.categorie} vaut ${formaterNombre(s.valeur)}`)
            .join(', ')
            .replace(/^./, (c) => c.toUpperCase()));
    }
    if (spec.titreAxeX.length > 0)
        phrases.push(`En abscisse : ${spec.titreAxeX}`);
    if (spec.titreAxeY.length > 0)
        phrases.push(`En ordonnée : ${spec.titreAxeY}`);
    return assembler(phrases);
}
/* ----------------------------------------------- la couche d'annotations (H1) */
/**
 * Les annotations sont des AFFIRMATIONS posées par-dessus la figure, pas des
 * propriétés de la figure (H1). La description le dit explicitement : sans
 * cela, la figure-erreur de G3 serait lue, au lecteur d'écran, comme un
 * énoncé — et enseignerait l'erreur qu'elle sert à montrer.
 */
function decrireAnnotations(spec, r) {
    const bouts = [];
    for (const a of (spec.annotations ?? []).slice(0, 6)) {
        switch (a.type) {
            case 'marque_angle_droit': {
                const position = ou(r, a.sommet);
                bouts.push(`une marque d'angle droit est posée au sommet ${a.sommet}${position === '' ? '' : `, ${position}`}`);
                break;
            }
            case 'cote_souligne':
                bouts.push(`le côté ${cote(a.de, a.a)} est tracé en gras${a.etiquette === undefined ? '' : ` et porte l'étiquette « ${a.etiquette} »`}`);
                break;
            case 'chemin':
                bouts.push(`un chemin passe par ${a.points.join(', ')}${a.etiquette === undefined ? '' : ` et porte l'étiquette « ${a.etiquette} »`}`);
                break;
            case 'note':
                bouts.push(`une note « ${a.texte} » est ajoutée`);
                break;
        }
    }
    if (bouts.length === 0)
        return '';
    return `Par-dessus la figure : ${bouts.join(' ; ')}.`;
}
/* ------------------------------------------------------------ point d'entrée */
/** Décrit une figure DÉJÀ résolue — la variante à préférer quand la résolution a déjà eu lieu. */
/** Le nom courant d'une base, pour être dit à voix haute. */
const NOM_BASE = {
    triangle: 'triangulaire',
    carre: 'carrée',
    rectangle: 'rectangulaire',
    hexagone: 'hexagonale',
};
/**
 * Un solide, dit à voix haute.
 *
 * Pour un élève dyslexique et pour un lecteur d'écran, cette phrase EST
 * l'énoncé — c'est la deuxième règle du contrat. Sans elle, une figure de
 * solide se lisait comme une chaîne vide : la famille tombait dans le `default`
 * du `switch`, silencieusement.
 *
 * Ce qui est dit et ce qui ne l'est pas suit la même règle que le tracé : les
 * dimensions DONNÉES sont énoncées, les dimensions dérivées ne le sont pas.
 * L'apothème d'un cône n'est nommé que s'il est explicitement tracé.
 */
function decrireSolide(spec, _r) {
    const d = spec.dimensions;
    const u = d.unite;
    const phrases = [];
    if (spec.vue === 'developpement') {
        phrases.push('Le développement à plat de la surface du solide');
    }
    switch (d.forme) {
        case 'cube':
            phrases.push(`Un cube d'arête ${mes(d.arete, u)}`);
            break;
        case 'prisme_droit':
            phrases.push(`Un prisme droit à base ${NOM_BASE[d.base] ?? d.base}, de hauteur ${mes(d.hauteur, u)}`, d.base === 'rectangle' && d.profondeurBase !== undefined
                ? `Sa base mesure ${mes(d.coteBase, u)} sur ${mes(d.profondeurBase, u)}`
                : `Le côté de sa base mesure ${mes(d.coteBase, u)}`);
            break;
        case 'pyramide':
            phrases.push(`Une pyramide droite à base ${NOM_BASE[d.base] ?? d.base}, de hauteur ${mes(d.hauteur, u)}`, d.base === 'rectangle' && d.profondeurBase !== undefined
                ? `Sa base mesure ${mes(d.coteBase, u)} sur ${mes(d.profondeurBase, u)}`
                : `Le côté de sa base mesure ${mes(d.coteBase, u)}`);
            break;
        case 'cylindre':
            phrases.push(`Un cylindre droit de rayon ${mes(d.rayon, u)} et de hauteur ${mes(d.hauteur, u)}`);
            break;
        case 'cone':
            phrases.push(`Un cône droit de rayon ${mes(d.rayon, u)} et de hauteur ${mes(d.hauteur, u)}`);
            break;
        default:
            phrases.push(`Une sphère de rayon ${mes(d.rayon, u)}`);
    }
    /* Ce que le pointillé veut dire. Un lecteur d'écran ne voit pas la
       différence entre un trait plein et un trait tireté ; sans cette phrase, la
       description d'un cube tairait la moitié de ce qui le fait lire comme un
       volume. */
    if (spec.vue === 'developpement') {
        phrases.push('Les traits pointillés marquent les plis');
    }
    else if (spec.aretesCacheesVisibles === false) {
        phrases.push('Les arêtes cachées ne sont pas tracées');
    }
    else if (d.forme !== 'sphere') {
        phrases.push('Les traits pointillés sont les arêtes cachées');
    }
    else {
        phrases.push('Le trait pointillé est la moitié arrière de l’équateur');
    }
    for (const r of spec.reperes ?? []) {
        if (r === 'hauteur')
            phrases.push('La hauteur est tracée en pointillé');
        if (r === 'rayon')
            phrases.push('Le rayon de la base est tracé en pointillé');
        if (r === 'apotheme')
            phrases.push('L’apothème est tracé en pointillé');
        if (r === 'diagonale_base')
            phrases.push('La diagonale de la base est tracée en pointillé');
    }
    return phrases.join('. ');
}
export function decrireFigureResolue(spec, resolue) {
    let corps;
    switch (spec.famille) {
        case 'triangle':
            corps = decrireTriangle(spec, resolue);
            break;
        case 'cercle':
            corps = decrireCercle(spec, resolue);
            break;
        case 'polygone':
            corps = decrirePolygone(spec, resolue);
            break;
        case 'repere':
            corps = decrireRepere(spec, resolue);
            break;
        case 'diagramme':
            corps = decrireDiagramme(spec, resolue);
            break;
        case 'solide':
            corps = decrireSolide(spec, resolue);
            break;
        default:
            corps = '';
    }
    const annotations = decrireAnnotations(spec, resolue);
    return annotations === '' ? corps : `${corps} ${annotations}`;
}
/**
 * La phrase unique servie à trois endroits : `accessibilityLabel` du SVG,
 * légende visible `.fig-cap`, texte alternatif de l'image du courriel.
 *
 * @returns `null` si la figure est insoluble — elle n'est alors pas affichée
 * du tout (§3.4), et il n'y a rien à décrire.
 */
export function decrireFigure(spec) {
    const resolue = resoudreFigure(spec);
    if (resolue === null)
        return null;
    return decrireFigureResolue(spec, resolue);
}
/**
 * Décrit le geste soumis par l'élève, en unités de figure (§7.2). Sert la même
 * exigence d'accessibilité que `decrireFigure` : ce que l'élève vient de poser
 * doit être lisible au lecteur d'écran, pas seulement visible.
 */
export function decrireGeste(geste, _resolue) {
    switch (geste.type) {
        case 'point':
            return `Un point est posé en ${coord(geste.x, geste.y)}.`;
        case 'segment':
            return `Un segment relie ${coord(geste.de.x, geste.de.y)} et ${coord(geste.a.x, geste.a.y)}.`;
        case 'vecteur':
            return `Un vecteur part de ${coord(geste.origine.x, geste.origine.y)} et arrive en ${coord(geste.extremite.x, geste.extremite.y)}.`;
        case 'parametres':
            return `Paramètres réglés : ${['a', 'b', 'c', 'd']
                .map((k) => `${k} = ${formaterNombre(geste.valeurs[k])}`)
                .join(', ')}.`;
        default:
            return '';
    }
}
//# sourceMappingURL=decrire.js.map